import type { QueryDisplay, QueryResult, QueryResultColumn } from './types';

const CLIP_KINDS = new Set(['id', 'url', 'email', 'phone']);
const STEP_CH = 1;
const MIN_CH = 6;
const MAX_CH = 48;

/** Default column widths, in ch, from the Customer-list measurement pass. */
export const COLUMN_CH: Record<string, number> = {
    text: 18, id: 12, choice: 14, multichoice: 14, email: 22, url: 22,
    number: 12, integer: 12, decimal: 12, currency: 12, percent: 10,
    date: 12, datetime: 18, boolean: 8, lookup: 16, user: 16, team: 16, phone: 14,
};

export function savedColumnWidthCh(column: QueryResultColumn, display?: QueryDisplay | null): number | undefined {
    const width = display?.columns?.[column.alias]?.widthCh;
    return typeof width === 'number' && Number.isFinite(width) && width >= MIN_CH && width <= MAX_CH ? width : undefined;
}

/** A column's width in ch as it opens: its saved width, else the default for its kind. */
export function columnWidthCh(column: QueryResultColumn, display?: QueryDisplay | null): number {
    return savedColumnWidthCh(column, display) ?? COLUMN_CH[columnKind(column)] ?? COLUMN_CH.text;
}

/**
 * The column resize control: a vertical separator that exposes its width as a value, so a screen reader hears the width
 * as it changes (ANX-1277). Arrow keys step by 1ch; Home and End go to the narrowest and widest allowed widths.
 */
export function resizeHandleMarkup(label: string, widthCh: number): string {
    const now = Math.round(Math.min(MAX_CH, Math.max(MIN_CH, widthCh)));
    return `<div role="separator" tabindex="0" class="analytics-col-resize" aria-orientation="vertical" aria-label="Resize ${label} column"`
        + ` aria-valuemin="${MIN_CH}" aria-valuemax="${MAX_CH}" aria-valuenow="${now}" aria-valuetext="${valueText(now)}"></div>`;
}

const valueText = (widthCh: number): string => `${widthCh} ${widthCh === 1 ? 'character' : 'characters'} wide`;

function setHandleValue(handle: HTMLElement, widthCh: number): void {
    const now = Math.round(Math.min(MAX_CH, Math.max(MIN_CH, widthCh)));
    handle.setAttribute('aria-valuenow', String(now));
    handle.setAttribute('aria-valuetext', valueText(now));
}

export function tableWidthCh(result: QueryResult, display: QueryDisplay | null = result.display ?? null): number {
    return result.columns.reduce((sum, column) => sum + (savedColumnWidthCh(column, display) ?? COLUMN_CH[columnKind(column)] ?? COLUMN_CH.text), 0);
}

export function columnKind(column: QueryResultColumn): string {
    if (column.fieldKind) return column.fieldKind;
    if (column.dataType === 'uuid') return 'id';
    if (column.dataType === 'number') return 'number';
    if (column.dataType === 'date') return 'date';
    if (column.dataType === 'boolean') return 'boolean';
    return 'text';
}

export function isIdentityColumn(result: QueryResult, index: number): boolean {
    return result.reportType === 'recordList' && index === 0;
}

export function clipsKind(kind: string, wrap?: boolean): boolean {
    return !wrap && CLIP_KINDS.has(kind);
}

/** Pointer and keyboard column resize, plus a focusable clip for truncated values. */
export function enhanceAnalyticsTable(root: HTMLElement, onResize?: (index: number, widthCh: number) => void): () => void {
    const table = root.querySelector<HTMLTableElement>('.analytics-table');
    if (!table) return () => {};
    if (table.dataset.widthCh) table.style.setProperty('--analytics-table-width', `${table.dataset.widthCh}ch`);
    // Saved column widths travel as data and are applied here, like the table width above.
    table.querySelectorAll<HTMLTableColElement>('col[data-width-ch]').forEach(col => { col.style.width = `${col.dataset.widthCh}ch`; });
    const abort = new AbortController();
    const { signal } = abort;
    table.querySelectorAll<HTMLElement>('.analytics-col-resize').forEach((handle, index) => {
        const col = table.querySelectorAll('col')[index];
        if (!col) return;
        // The width as laid out, which the keyboard steps from; markup carries the nominal width until layout exists.
        const laidOut = col.getBoundingClientRect().width;
        if (laidOut > 0) setHandleValue(handle, laidOut / chPx(col));
        const resize = (width: number) => {
            const widthCh = Math.round(Math.min(MAX_CH, Math.max(MIN_CH, width / chPx(col))) * 100) / 100;
            setColWidth(table, col, widthCh * chPx(col));
            setHandleValue(handle, widthCh);
            onResize?.(index, widthCh);
        };
        handle.addEventListener('pointerdown', event => {
            if (event.button !== 0) return;
            event.preventDefault();
            handle.setPointerCapture(event.pointerId);
            const startX = event.clientX;
            const start = col.getBoundingClientRect().width;
            const move = (next: PointerEvent) => { resize(start + next.clientX - startX); };
            const up = () => {
                handle.removeEventListener('pointermove', move);
                handle.removeEventListener('pointerup', up);
                handle.removeEventListener('lostpointercapture', up);
            };
            handle.addEventListener('pointermove', move, { signal });
            handle.addEventListener('pointerup', up, { signal });
            handle.addEventListener('lostpointercapture', up, { signal });
        }, { signal });
        handle.addEventListener('keydown', event => {
            const ch = chPx(col);
            const current = col.getBoundingClientRect().width;
            const next = event.key === 'ArrowRight' ? current + ch * STEP_CH
                : event.key === 'ArrowLeft' ? current - ch * STEP_CH
                : event.key === 'Home' ? MIN_CH * ch
                : event.key === 'End' ? MAX_CH * ch
                : null;
            if (next == null) return;
            event.preventDefault();
            resize(next);
        }, { signal });
    });
    root.querySelectorAll<HTMLElement>('.analytics-cell-clip').forEach(clip => {
        if (clip.scrollWidth > clip.clientWidth + 1) {
            clip.title = clip.textContent?.trim() ?? '';
            clip.dataset.clipped = 'true';
        }
    });
    return () => abort.abort();
}

function setColWidth(table: HTMLTableElement, col: HTMLElement, width: number): void {
    const previous = col.getBoundingClientRect().width;
    const tableWidth = table.getBoundingClientRect().width + (width - previous);
    col.style.width = `${width}px`;
    table.style.setProperty('--analytics-table-width', `${Math.max(0, tableWidth)}px`);
}

function chPx(el: Element): number {
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;font:inherit;width:1ch';
    // A col cannot lay out children; measure using the table font outside it.
    probe.style.font = getComputedStyle(el.closest('table') ?? el).font;
    document.body.appendChild(probe);
    const px = probe.getBoundingClientRect().width || 8;
    probe.remove();
    return px;
}
